import React, { FunctionComponent, useEffect, useState } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { RadioGroup, RadioGroupValue } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { ShieldTriageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ShieldTriageIcon'
import { TextBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TextBadge'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { BadgeShape, BadgeVariant } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

type VideoSessionPreferenceEditModalProps = {
  visible: boolean
  currentPlatform: VIDEO_PLATFORM
  isLoadingAppointments?: boolean
  onRequestClose: () => void
  onSave: (platform: VIDEO_PLATFORM) => Promise<void>
}

const RadioGroupContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['8px'],
}))

const LoadingContainer = styled.View(({ theme }) => ({
  paddingVertical: theme.spacing['48px'],
  width: '100%',
}))

const LabelContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

export const VideoSessionPreferenceEditModal: FunctionComponent<VideoSessionPreferenceEditModalProps> = ({
  visible,
  currentPlatform,
  isLoadingAppointments = false,
  onRequestClose,
  onSave,
}) => {
  const { colors } = useTheme() as ThemeType
  const [selectedPlatform, setSelectedPlatform] = useState<VIDEO_PLATFORM>(currentPlatform)

  useEffect(() => {
    if (visible) {
      setSelectedPlatform(currentPlatform)
    }
  }, [visible, currentPlatform])

  const radioButtons = [
    {
      label: (
        <LabelContainer>
          <BodyText color={colors.radioWithOutlinePrimaryTextDefault}>
            {VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.LYRA]}
          </BodyText>
          <Tooltip content='HIPAA Compliant' hoverEnabled placement='top'>
            <ShieldTriageIcon size={20} color={colors.radioWithOutlinePrimaryTextDefault} />
          </Tooltip>
          <TextBadge text='No cost' variant={BadgeVariant.SUCCESS} shape={BadgeShape.RECT} />
        </LabelContainer>
      ),
      value: VIDEO_PLATFORM.LYRA,
      description: (
        <BodyText color={colors.radioWithOutlineSecondaryTextDefault}>
          <BodyText bold color={colors.radioWithOutlineSecondaryTextDefault}>
            {'Automated and secure. '}
          </BodyText>
          {
            'Lyra handles link generation and session reminders automatically. You and your client join sessions from the HIPAA compliant Lyra platform.'
          }
        </BodyText>
      ),
    },
    {
      label: VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.EXTERNAL],
      value: VIDEO_PLATFORM.EXTERNAL,
      description: 'You are responsible for manually sending external meeting links to clients for every session.',
    },
  ]

  return (
    <ConfirmationModal
      testID={tID('VideoSessionPreferenceEditModal')}
      visible={visible}
      onRequestClose={onRequestClose}
      modalTitle='Edit video session preference'
      modalContents={
        isLoadingAppointments ? (
          <LoadingContainer testID={tID('VideoSessionPreferenceEditModal-loading')}>
            <LoadingIndicator topPadding={0} />
          </LoadingContainer>
        ) : (
          <RadioGroupContainer>
            <RadioGroup
              buttons={radioButtons}
              selectedValue={selectedPlatform}
              onChange={(value: RadioGroupValue) => setSelectedPlatform(value as VIDEO_PLATFORM)}
              buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
            />
          </RadioGroupContainer>
        )
      }
      confirmationButtonText='Save'
      cancelButtonText='Cancel'
      confirmButtonDisabled={isLoadingAppointments}
      onConfirmationButtonPress={() => onSave(selectedPlatform)}
    />
  )
}
